import type { SessionKind } from '../stores/session/sessionStore';

/**
 * Host detection for the in-app dev tools overlay.
 *
 * The overlay exists only when the app runs inside the Acomba desktop host — acceo_web.exe, an
 * out-of-process browser shell reparented into the Delphi MDI window, with no F12 and no way to attach
 * an external inspector. `sessionKind === 'desktop'` is the authoritative signal, but it is only set
 * once the desktop assertion has been exchanged, long after boot — so a launch on `/desktop` also drops
 * a marker, letting the overlay capture console output and network calls from the first frame.
 *
 * localStorage (not sessionStorage) for the same reason as the desktop assertion stash: acceo_web.exe
 * may open the OIDC callback in a different tab, and sessionStorage is tab-local.
 */

const DESKTOP_HOST_KEY = 'devtools-desktop-host';

/** The desktop SSO entry route (`/desktop?src=…`), the earliest observable sign of an Acomba launch. */
const DESKTOP_LAUNCH_PATH = '/desktop';

/**
 * How long the marker stands in for a session that has not resolved yet.
 *
 * The window it has to cover is the OIDC round-trip: `/desktop` redirects to Identity Server at once, so
 * the marker is read back on `/signin-callback` — a different page, after the user has finished signing
 * in. Ten minutes is generous for that, and short enough that a marker left by an earlier launch cannot
 * arm the overlay in a plain browser session hours later.
 */
const MARKER_TTL_MS = 10 * 60 * 1000;

/** Records that an Acomba desktop launch is underway. No-op on any other route. */
export function markDesktopHostFromLocation(): void {
    if (window.location.pathname !== DESKTOP_LAUNCH_PATH) return;
    try {
        localStorage.setItem(DESKTOP_HOST_KEY, String(Date.now()));
    } catch {
        // Storage can throw in private mode / on quota. The marker only widens the boot-time window in
        // which the overlay is available, so losing it degrades gracefully to the sessionKind signal.
    }
}

/** Whether a desktop launch left a marker that has not yet expired. */
export function hasDesktopHostMarker(): boolean {
    try {
        const raw = localStorage.getItem(DESKTOP_HOST_KEY);
        if (raw === null) return false;
        const markedAt = Number(raw);
        return Number.isFinite(markedAt) && Date.now() - markedAt < MARKER_TTL_MS;
    } catch {
        return false;
    }
}

/** Drops the marker once a session resolves, during this page's lifetime, to something else. */
export function clearDesktopHostMarker(): void {
    try {
        localStorage.removeItem(DESKTOP_HOST_KEY);
    } catch {
        // See markDesktopHostFromLocation — storage failures are non-fatal here.
    }
}

/**
 * Whether the app is running inside the Acomba desktop host.
 *
 * A resolved `desktop` session is proof. Otherwise an unexpired launch marker stands in — and it outranks
 * a `browser` / `portal` value, because `sessionKind` is persisted: at boot it describes the *previous*
 * session on this profile, so it can still read `browser` while an Acomba launch is mid-flight. The only
 * live evidence that a non-desktop session has taken over is a `sessionKind` change observed during this
 * page's lifetime, and `initDevTools` clears the marker when it sees one.
 */
export function isAcombaDesktopHost(sessionKind: SessionKind): boolean {
    if (sessionKind === 'desktop') return true;
    return hasDesktopHostMarker();
}
